import React from 'react';
import { format } from 'date-fns';
import { FilePlus, Edit3, CheckCircle2, XCircle, AlertTriangle, ArrowRight } from 'lucide-react';

export interface TrailEntry {
  id: string;
  action: string;
  user: string;
  role: string;
  timestamp: string;
  note?: string;
  type: 'create' | 'update' | 'approve' | 'close' | 'reject' | 'review';
}

const TYPE_CONFIG = {
  create:  { icon: FilePlus,      color: '#16A34A', bg: '#F0FDF4', border: '#16A34A' },
  update:  { icon: Edit3,         color: '#D97706', bg: '#FFFBEB', border: '#D97706' },
  approve: { icon: CheckCircle2,  color: '#2563EB', bg: '#EFF6FF', border: '#2563EB' },
  close:   { icon: CheckCircle2,  color: '#16A34A', bg: '#F0FDF4', border: '#16A34A' },
  reject:  { icon: XCircle,       color: '#DC2626', bg: '#FEF2F2', border: '#DC2626' },
  review:  { icon: ArrowRight,    color: '#7C3AED', bg: '#F5F3FF', border: '#7C3AED' },
};

interface AuditTrailProps {
  trail?: TrailEntry[];
  createdBy?: { name: string; role: string };
  createdAt?: string;
  updatedBy?: { name: string; role: string };
  updatedAt?: string;
}

export default function AuditTrail({ trail, createdBy, createdAt, updatedBy, updatedAt }: AuditTrailProps) {
  return (
    <div style={{ borderTop: '1px solid rgba(0,0,0,0.04)', paddingTop: 14 }}>
      {/* Attribution header — ALCOA "Attributable" */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: '#5a5a6e', textTransform: 'uppercase', letterSpacing: '1px' }}>
          Data Integrity Trail
        </p>
        <span style={{
          fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
          background: '#F0FDF4', color: '#16A34A', border: '1px solid rgba(22,163,74,0.2)',
        }}>ALCOA+</span>
      </div>

      {/* Attribution cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
        {createdBy && createdAt && (
          <div style={{ padding: '8px 10px', borderRadius: 8, background: '#f7f5f2', border: '1px solid rgba(0,0,0,0.1)' }}>
            <p style={{ fontSize: 9.5, color: '#94A3B8', marginBottom: 3, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
              Initiated By
            </p>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e', marginBottom: 1 }}>{createdBy.name}</p>
            <p style={{ fontSize: 10, color: '#5a5a6e', marginBottom: 2 }}>{createdBy.role}</p>
            <p style={{ fontSize: 10, color: '#94A3B8', fontFamily: 'monospace' }}>
              {format(new Date(createdAt), 'd MMM yyyy, HH:mm')}
            </p>
          </div>
        )}
        {updatedBy && updatedAt && (
          <div style={{ padding: '8px 10px', borderRadius: 8, background: '#f7f5f2', border: '1px solid rgba(0,0,0,0.1)' }}>
            <p style={{ fontSize: 9.5, color: '#94A3B8', marginBottom: 3, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
              Last Modified By
            </p>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e', marginBottom: 1 }}>{updatedBy.name}</p>
            <p style={{ fontSize: 10, color: '#5a5a6e', marginBottom: 2 }}>{updatedBy.role}</p>
            <p style={{ fontSize: 10, color: '#94A3B8', fontFamily: 'monospace' }}>
              {format(new Date(updatedAt), 'd MMM yyyy, HH:mm')}
            </p>
          </div>
        )}
      </div>

      {/* Event timeline */}
      {trail && trail.length > 0 && (
        <div style={{ position: 'relative', paddingLeft: 28 }}>
          {/* Vertical line */}
          <div style={{
            position: 'absolute', left: 10, top: 11, bottom: 8,
            width: 1, background: 'linear-gradient(to bottom, rgba(0,0,0,0.1), transparent)',
          }} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {trail.map((entry, idx) => {
              const cfg = TYPE_CONFIG[entry.type] || TYPE_CONFIG.update;
              const Icon = cfg.icon;
              return (
                <div key={entry.id} style={{ position: 'relative' }}>
                  {/* Dot */}
                  <div style={{
                    position: 'absolute', left: -28, top: 1,
                    width: 20, height: 20, borderRadius: '50%',
                    background: cfg.bg, border: `2px solid ${cfg.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    opacity: idx === 0 ? 1 : 0.75,
                  }}>
                    <Icon size={9} color={cfg.color} strokeWidth={2.5} />
                  </div>

                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: '#1a1a2e' }}>{entry.action}</span>
                      <span style={{ fontSize: 10, color: '#94A3B8', fontFamily: 'monospace', flexShrink: 0, marginLeft: 8 }}>
                        {format(new Date(entry.timestamp), 'd MMM, HH:mm')}
                      </span>
                    </div>
                    <p style={{ fontSize: 11, color: '#5a5a6e' }}>
                      {entry.user}
                      <span style={{ color: '#94A3B8' }}> · {entry.role}</span>
                    </p>
                    {entry.note && (
                      <p style={{ fontSize: 11, color: '#5a5a6e', marginTop: 3, padding: '4px 8px', background: '#f7f5f2', borderRadius: 5, borderLeft: `2px solid ${cfg.border}` }}>
                        {entry.note}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
